import { useCallback, useMemo } from 'react';
import type { ModuleGovernanceModule, ModuleGovernanceState } from '@triedge/staff-settings-domain';
import { useStaffSettingsState } from './useStaffSettingsState';
import {
  materializeModuleGovernanceState,
  type GovernanceScope,
} from '../../../platform-governance/moduleGovernanceCatalog';

type ModuleCollectionKey = 'schoolModules' | 'parentModules';

const scopeToCollectionKey = (scope: GovernanceScope): ModuleCollectionKey =>
  scope === 'parent' ? 'parentModules' : 'schoolModules';

const cloneModule = (module: ModuleGovernanceModule): ModuleGovernanceModule => ({
  moduleId: module.moduleId,
  enabled: module.enabled,
  routeGroups: module.routeGroups.map((routeGroup) => ({
    routeGroupId: routeGroup.routeGroupId,
    enabled: routeGroup.enabled,
  })),
});

const toPersistedModule = (module: {
  moduleId: string;
  enabled: boolean;
  routeGroups: Array<{ routeGroupId: string; enabled: boolean }>;
}): ModuleGovernanceModule => ({
  moduleId: module.moduleId,
  enabled: module.enabled,
  routeGroups: module.routeGroups.map((routeGroup) => ({
    routeGroupId: routeGroup.routeGroupId,
    enabled: routeGroup.enabled,
  })),
});

export const useModuleEnablementState = () => {
  const { settingsState, persistPatch, isLoading, isSaving } = useStaffSettingsState();

  const moduleEnablementState = useMemo(
    () => materializeModuleGovernanceState(settingsState.governance),
    [settingsState.governance],
  );

  const persistScopePatch = useCallback(
    async (
      scope: GovernanceScope,
      patcher: (current: ModuleGovernanceModule[]) => ModuleGovernanceModule[],
    ) => {
      const collectionKey = scopeToCollectionKey(scope);
      await persistPatch((current) => {
        const currentGovernance: ModuleGovernanceState = {
          schoolModules: materializeModuleGovernanceState(current.governance).schoolModules.map(toPersistedModule),
          parentModules: materializeModuleGovernanceState(current.governance).parentModules.map(toPersistedModule),
        };
        const nextCollection = patcher(currentGovernance[collectionKey].map(cloneModule));
        current.governance = {
          ...currentGovernance,
          [collectionKey]: nextCollection.map(toPersistedModule),
        };
        return current;
      });
    },
    [persistPatch],
  );

  return {
    moduleEnablementState,
    persistScopePatch,
    isLoading,
    isSaving,
  };
};
